import {
  Badge,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputBase,
  Menu,
  Paper,
  Step,
  StepLabel,
  Stepper,
  SvgIcon,
  SvgIconProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  fade,
  makeStyles
} from '@material-ui/core/styles';
import {
  AddRounded,
  CloseRounded,
  DeleteRounded,
  EditRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowLeftRounded,
  KeyboardArrowRightRounded,
  MoreVertRounded,
  SearchRounded,
  UndoRounded
} from '@material-ui/icons';
import {
  Alert,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup
} from '@material-ui/lab';
import { KeyboardDatePicker } from '../../../components/CustomDatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import clsx from 'clsx';
import moment from 'moment';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { GetBulkPlanningDocumentParams } from '../../../api/documents';
import { BulkProjectDocument } from '../../../api/project-documents';
import { RootState } from '../../../app/store';
import { ProductNameLabel } from '../../../common/enum/ProductNameLabel';
import ConfirmDeleteBox from '../../../components/ConfirmDeleteBox';
import HelpTooltip from '../../../components/HelpTooltip';
import { DAYS_FOR_COMPLETE_REVIEW } from '../../../constants';
import { addWeekdays } from '../../../functions/addWeekdays';
import getNotFoundText from '../../../functions/getNotFoundText';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import { useQueryStringNumber } from '../../../hooks/useQueryString';
import AutoAwesomeRounded from '../../../icon/AutoAwesomeRounded';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  getBulkPlanningDocumentsAsync,
  getDocumentsBulkPlanningMasterDataAsync
} from '../../MasterData/Document/documentSlice';
import { getProjectAsync } from '../../Project/projectSlice';
import { User } from '../../UserProfile/userProfileSlice';
import { checkProductType } from '../DocumentCreate';
import { bulkCreateProjectDocumentsAsync } from '../projectDocumentSlice';
import { DisciplineLabel, PurposeLabel, RevisionLabel } from './index';

interface Params {
  disciplineIds: number[];
  search: string | null;
}

interface DocumentRevision {
  purposeId: number | null;
  revisionId: number | null;
}

interface BulkDocument {
  refId: number;
  name: string | null;
  no: string | null;
  accessControlId: number | null;
  documentCreatorId: number | null;
  disciplineId: number;
  documentId: number;
  isAIMS: boolean;
  projectId: number;
  projectDocumentRevisions: BulkDocumentRevision[];
  userIds: number[];
  consultantId?: number;
  contractorId?: number;
  subContractorId?: number;
}

interface BulkDocumentRevision {
  refId: number;
  planIssueDate: Date | null;
  planCompletedDate: Date | null;
  manHour: number | null;
  purposeId: number | null;
  revisionId: number | null;
  isAutoApproval: boolean;
}

interface HistoryDeleteDocumentRevision {
  refDocumentId: number;
  projectDocumentRevision: BulkDocumentRevision;
}

type ViewMode = 'expand' | 'collapse';

const stepKeys = [
  'bulkPlanning.steps.selectDocuments',
  'bulkPlanning.steps.customizeTemplate',
  'bulkPlanning.steps.createDocuments'
];

export default function OtherBulkPlanning() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const projectId = useQueryStringNumber({ query: 'projectId' });

  const { selectedProject } = useSelector((state: RootState) => state.projects);
  const {
    bulkPlanningMasterData: masterDataDocuments,
    bulkPlanning: templateDocuments
  } = useSelector((state: RootState) => state.documents);
  const { data: purposes } = useSelector((state: RootState) => state.purposes);
  const { data: revisions } = useSelector(
    (state: RootState) => state.revisions
  );
  const { data: accessControls } = useSelector(
    (state: RootState) => state.accessControls
  );
  const { data: documents } = useSelector(
    (state: RootState) => state.documents
  );
  const { users } = useSelector((state: RootState) => state.projectRegisters);
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.userProfile
  );

  const [selectedDocumentIds, setSelectedDocumentIds] = useState<number[]>([]); // Store `id` of document
  const [selectedDocumentRefIds, setSelectedDocumentRefIds] = useState<
    number[]
  >([]); // Store  `refId` of document
  const [selectedDocument, setSelectedDocument] = useState<BulkDocument | null>(
    null
  );
  const [params, setParams] = useState<Params>({
    disciplineIds: [],
    search: null
  });
  const [code, setCode] = useState<{
    plant: string | null;
  }>({
    plant: null
  });
  const defaultDocumentRevisions: DocumentRevision[] = [
    { purposeId: PurposeLabel.IFR, revisionId: RevisionLabel.A },
    { purposeId: PurposeLabel.IFC, revisionId: RevisionLabel.Zero },
    { purposeId: PurposeLabel.IFF, revisionId: RevisionLabel.One }
  ];
  const [documentRevisions, setDocumentRevisions] = useState<
    DocumentRevision[]
  >(defaultDocumentRevisions);
  const [bulkDocuments, setBulkDocuments] = useState<BulkDocument[]>([]);
  const [historyDeleteDocumentRevisions, setHistoryDeleteDocumentRevisions] =
    useState<HistoryDeleteDocumentRevision[]>([]);

  const [activeStep, setActiveStep] = useState(0);
  const [openedDocumentRefIds, setOpenedDocumentRefIds] = useState<number[]>(
    []
  );
  const [isEditRevision, setIsEditRevision] = useState<boolean>(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [isShowAllUsers, setIsShowAllUsers] = useState<boolean>(false);
  const [openDeleteOneDialog, setOpenDeleteOneDialog] =
    useState<boolean>(false);
  const [openDeleteAllDialog, setOpenDeleteAllDialog] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const originalNo = `XXX-REPCO-XXX-XXX`;

  useEffect(() => {
    // Prepare the document in the `Create Documents` step only
    if (selectedProject && activeStep === 2) {
      const projectDocuments = templateDocuments.map((document, index) => {
        let userIds: number[] = [];
        document.projectPositionWorkflowSteps.forEach(
          (projectPositionWorkflowStep) => {
            if (projectPositionWorkflowStep.projectPosition) {
              projectPositionWorkflowStep.projectPosition.userProjectPositions.forEach(
                (userProjectPosition) =>
                  userIds.push(userProjectPosition.userId)
              );
            }
          }
        );
        // Filter array to have unique values
        userIds = userIds.filter((item, pos) => {
          return userIds.indexOf(item) == pos;
        });

        let documentCreatorId = null;
        // Select document creator from logged in user. If logged in user isn't document creator list, the system will select the first user
        if (userIds.length > 0) {
          documentCreatorId =
            userIds.find((userId) => userId === loggedInUser?.id) ?? userIds[0];
        }

        const projectDocument: BulkDocument = {
          refId: index, // To prevent remove wrong record when deleting document after selecting
          name: document.name,
          no: document.no,
          accessControlId: 2,
          documentCreatorId,
          disciplineId: document.disciplineId,
          documentId: document.id,
          isAIMS: false,
          projectId: selectedProject.id,
          projectDocumentRevisions: documentRevisions.map(
            ({ purposeId, revisionId }, index) => {
              return {
                refId: index, // To record order of revision using for undo action
                planIssueDate: null,
                planCompletedDate: null,
                manHour: 1,
                purposeId,
                revisionId,
                isAutoApproval: purposeId === PurposeLabel.IFR
              };
            }
          ),
          userIds
        };

        // Magic for selecting REPCO as:
        const repcoId = 7;
        switch (checkProductType(selectedProject.productName)) {
          // 1. Consultant on PMC project
          case ProductNameLabel.pmc:
            projectDocument.consultantId = repcoId;
            break;
          // 2. Main contractor on EPC project
          case ProductNameLabel.epc:
            projectDocument.contractorId = repcoId;
            break;
          default:
            break;
        }

        return projectDocument;
      });
      setBulkDocuments(projectDocuments);
      setOpenedDocumentRefIds([0]);
    }
  }, [templateDocuments, activeStep]);

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectAsync(projectId));
    }
    loadingData({
      disciplineIds: [DisciplineLabel.POB],
      search: null
    });
  }, []);

  function loadingData(params: Params) {
    dispatch(getDocumentsBulkPlanningMasterDataAsync(params));
    setParams(params);
  }

  function handleChangeSearch(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const keyword = event.target.value;
    const search = keyword === '' ? null : keyword;
    loadingData({ ...params, search });
  }

  function modifiedNo(originalNo: string): string {
    let arrayFromOriginalNo = originalNo.split('-');
    // Replace plant name (at Position 0, Remove 1 item, and add `plant`)
    if (code.plant) arrayFromOriginalNo.splice(0, 1, code.plant.toUpperCase());
    // @See more: https://www.w3schools.com/jsref/jsref_splice.asp
    return arrayFromOriginalNo.join('-');
  }

  function generateRevisionChip(
    { purposeId, revisionId }: DocumentRevision,
    key: number
  ): ReactNode {
    const purpose = purposes.find((purpose) => purpose.id === purposeId);
    const revision = revisions.find((revision) => revision.id === revisionId);

    return (
      <React.Fragment key={key}>
        <Tooltip
          title={t('bulkPlanning.pob.purposeRevisionTooltip', { purposeFullName: purpose?.fullName, revisionName: revision?.name }) as string}
          arrow
        >
          <Chip
            className={classes.truncatedChip}
            variant="outlined"
            label={t('bulkPlanning.pob.purposeRevisionLabel', { purposeAbbr: purpose?.abbreviation, revisionName: revision?.name })}
          />
        </Tooltip>
      </React.Fragment>
    );
  }

  function nextButton(step: number): { disabled: boolean; action: any } {
    switch (step) {
      case 0:
        return {
          disabled: selectedDocumentIds.length === 0,
          action: handleNext
        };
      case 1:
        return {
          disabled: isEditRevision || loading,
          action: async () => {
            try {
              setLoading(true);
              if (projectId) {
                const params: GetBulkPlanningDocumentParams = {
                  projectId,
                  disciplineId: DisciplineLabel.POB,
                  ids: selectedDocumentIds,
                  plant: code.plant
                };
                await dispatch(getBulkPlanningDocumentsAsync(params)).unwrap();
                setOpenedDocumentRefIds([]);
                setSelectedDocumentRefIds([]);
                setHistoryDeleteDocumentRevisions([]);
                setTimeout(() => {
                  handleNext();
                  setLoading(false);
                }, 250);
              }
            } catch {
              setLoading(false);
            }
          }
        };
      case 2:
        return {
          disabled:
            bulkDocuments.length === 0 ||
            bulkDocuments.some(
              (document) =>
                !document.name ||
                !document.no ||
                !document.accessControlId ||
                !document.documentCreatorId ||
                document.projectDocumentRevisions.some(
                  (documentRevision) =>
                    !documentRevision.purposeId ||
                    !documentRevision.revisionId ||
                    !documentRevision.manHour ||
                    !documentRevision.planIssueDate ||
                    !documentRevision.planCompletedDate
                )
            ) ||
            loading,
          action: handleNext // The action in this step doesn't use, we will use `onSubmit` function instead
        };
      default:
        return { disabled: true, action: handleNext };
    }
  }

  function handleNext(): void {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }

  function handleBack(): void {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }

  function handleEditRevision(): void {
    setIsEditRevision((prevEdit) => !prevEdit);
  }

  function handleChangeDocumentView(
    event: React.MouseEvent<HTMLElement>,
    newValue: ViewMode
  ) {
    if (newValue === 'expand') {
      const allItems = bulkDocuments.map((document) => document.refId);
      setOpenedDocumentRefIds(allItems);
    } else if (newValue === 'collapse') {
      setOpenedDocumentRefIds([]);
    }
  }

  function getToggleStatus(
    selectedItems: number,
    allItems: number
  ): ViewMode | null {
    if (selectedItems === 0) return 'collapse';
    if (selectedItems === allItems) return 'expand';
    return null;
  }

  function handleOpenDocument(refId: number): void {
    const open = getStatus(refId, openedDocumentRefIds);
    if (open) {
      setOpenedDocumentRefIds(
        openedDocumentRefIds.filter(
          (openedDocumentRefId) => openedDocumentRefId !== refId
        )
      );
    } else {
      setOpenedDocumentRefIds([...openedDocumentRefIds, refId]);
    }
  }

  function handleSelectTemplate(id: number): void {
    const selected = getStatus(id, selectedDocumentIds);
    if (selected) {
      setSelectedDocumentIds(
        selectedDocumentIds.filter(
          (selectedDocumentId) => selectedDocumentId !== id
        )
      );
    } else {
      setSelectedDocumentIds([...selectedDocumentIds, id]);
    }
  }

  function handleSelectDocument(refId: number): void {
    const selected = getStatus(refId, selectedDocumentRefIds);
    if (selected) {
      setSelectedDocumentRefIds(
        selectedDocumentRefIds.filter(
          (selectedDocumentRefId) => selectedDocumentRefId !== refId
        )
      );
    } else {
      setSelectedDocumentRefIds([...selectedDocumentRefIds, refId]);
    }
  }

  function handleSelectAllClick(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    if (event.target.checked) {
      const selectedDocumentRefIds = bulkDocuments.map(
        (document) => document.refId
      );
      setSelectedDocumentRefIds(selectedDocumentRefIds);
    } else {
      setSelectedDocumentRefIds([]);
    }
  }

  function getStatus(value: number, selectedItems: number[]): boolean {
    return selectedItems.indexOf(value) !== -1;
  }

  function getValue<T extends { id: number }>(
    id: number | null,
    options: T[]
  ): T | null {
    return options.find((option) => option.id === id) ?? null;
  }

  function handleChangeTextField(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const { name, value } = event.target;
    setCode({
      ...code,
      [name]: value === '' ? null : value.toUpperCase().substring(0, 3)
    });
  }

  function handleChangeRevisionTemplate(
    value: number | undefined,
    field: 'purposeId' | 'revisionId',
    index: number
  ): void {
    const documentRevision = documentRevisions[index];
    const newDocumentRevision = {
      ...documentRevision,
      [field]: value ?? null
    };

    // To reset field in purpose field is changed
    if (field === 'purposeId') newDocumentRevision.revisionId = null;

    const newDocumentRevisions = [
      ...documentRevisions.slice(0, index),
      newDocumentRevision,
      ...documentRevisions.slice(index + 1)
    ];
    setDocumentRevisions(newDocumentRevisions);
  }

  function handleRemoveRevisionTemplate(index: number): void {
    const newDocumentRevisions = [
      ...documentRevisions.slice(0, index),
      ...documentRevisions.slice(index + 1)
    ];
    setDocumentRevisions(newDocumentRevisions);
  }

  function handleNewRevisionTemplate(): void {
    setDocumentRevisions([
      ...documentRevisions,
      { purposeId: null, revisionId: null }
    ]);
  }

  function handleResetRevisionTemplate(): void {
    setDocumentRevisions(defaultDocumentRevisions);
  }

  function handleAnchorElement(
    open: boolean
  ): (event: React.MouseEvent<HTMLButtonElement>) => void {
    return function (event: React.MouseEvent<HTMLButtonElement>) {
      setAnchorElement(open ? event.currentTarget : null);
    };
  }

  function getRelatedUser(userIds: number[], users: User[]): User[] {
    return !isShowAllUsers
      ? users.filter((user) => userIds.some((userId) => userId === user.id))
      : users;
  }

  function handleChangeRelatedUsers(): void {
    setIsShowAllUsers((prev) => !prev);
  }

  function handleChangeDocumentDetail(
    value: string | number,
    field: 'name' | 'no' | 'documentCreatorId',
    index: number
  ): void {
    const document = bulkDocuments[index];
    const newDocument = { ...document, [field]: value === '' ? null : value };

    const newDocuments = [
      ...bulkDocuments.slice(0, index),
      newDocument,
      ...bulkDocuments.slice(index + 1)
    ];
    setBulkDocuments(newDocuments);
  }

  function handleChangeDocumentRevisionDetail(
    value: string | number | boolean | MaterialUiPickersDate,
    field:
      | 'purposeId'
      | 'revisionId'
      | 'isAutoApproval'
      | 'manHour'
      | 'planIssueDate'
      | 'planCompletedDate',
    documentIndex: number,
    documentRevisionIndex: number,
    options?: { positiveNumber: boolean }
  ): void {
    const documentRevision =
      bulkDocuments[documentIndex].projectDocumentRevisions[
        documentRevisionIndex
      ];
    const newDocumentRevision = {
      ...documentRevision,
      [field]:
        options?.positiveNumber && typeof value === 'string'
          ? Number.parseFloat(value) <= 0
            ? null
            : Number.parseFloat(value)
          : value === ''
          ? null
          : value
    };

    if (field === 'purposeId') {
      // To reset revision field in purpose field is changed
      newDocumentRevision.revisionId = null;
      // To default check `isAutoApproval` if purpose is `IFR`
      newDocumentRevision.isAutoApproval = value === PurposeLabel.IFR;
    }

    if (field === 'planIssueDate') {
      if (value instanceof Date) {
        newDocumentRevision.planCompletedDate = addWeekdays(
          value,
          DAYS_FOR_COMPLETE_REVIEW
        );
      } else {
        newDocumentRevision.planCompletedDate = null;
      }
    }

    const newDocumentRevisions = [
      ...bulkDocuments[documentIndex].projectDocumentRevisions.slice(
        0,
        documentRevisionIndex
      ),
      newDocumentRevision,
      ...bulkDocuments[documentIndex].projectDocumentRevisions.slice(
        documentRevisionIndex + 1
      )
    ];

    const newDocument = {
      ...bulkDocuments[documentIndex],
      projectDocumentRevisions: newDocumentRevisions
    };
    const newDocuments = [
      ...bulkDocuments.slice(0, documentIndex),
      newDocument,
      ...bulkDocuments.slice(documentIndex + 1)
    ];
    setBulkDocuments(newDocuments);
  }

  function handleOpenDeleteOneDialog(document?: BulkDocument): void {
    setOpenDeleteOneDialog((prev) => !prev);
    if (document) setSelectedDocument(document);
  }

  function handleOpenDeleteAllDialog(): void {
    setOpenDeleteAllDialog((prev) => !prev);
  }

  async function onReset(): Promise<void> {
    if (projectId) {
      const params: GetBulkPlanningDocumentParams = {
        projectId,
        disciplineId: DisciplineLabel.POB,
        plant: code.plant
      };
      await dispatch(getBulkPlanningDocumentsAsync(params)).unwrap();
    }
  }

  function onDeleteDocument(): void {
    if (selectedDocument) {
      const newDocuments = bulkDocuments.filter(
        (document) => document.refId !== selectedDocument.refId
      );
      const newOpenedDocumentRefIds = openedDocumentRefIds.filter(
        (refId) => selectedDocument.refId !== refId
      );
      const newHistoryDeleteDocumentRevisions =
        historyDeleteDocumentRevisions.filter(
          ({ refDocumentId }) => selectedDocument.refId !== refDocumentId
        );
      const newSelectedDocumentRefIds = selectedDocumentRefIds.filter(
        (refId) => selectedDocument.refId !== refId
      );
      setBulkDocuments(newDocuments);
      setOpenedDocumentRefIds(newOpenedDocumentRefIds);
      setHistoryDeleteDocumentRevisions(newHistoryDeleteDocumentRevisions);
      setSelectedDocumentRefIds(newSelectedDocumentRefIds);
      setOpenDeleteOneDialog(false);
    }
  }

  function onDeleteSelectedDocuments(): void {
    const newDocuments = bulkDocuments.filter((document) =>
      selectedDocumentRefIds.every((refId) => refId !== document.refId)
    );
    const newOpenedDocumentRefIds = openedDocumentRefIds.filter((id) =>
      selectedDocumentRefIds.every((refId) => refId !== id)
    );
    const newHistoryDeleteDocumentRevisions =
      historyDeleteDocumentRevisions.filter(({ refDocumentId }) =>
        selectedDocumentRefIds.every((refId) => refId !== refDocumentId)
      );
    setBulkDocuments(newDocuments);
    setOpenedDocumentRefIds(newOpenedDocumentRefIds);
    setHistoryDeleteDocumentRevisions(newHistoryDeleteDocumentRevisions);
    setSelectedDocumentRefIds([]);
    setOpenDeleteAllDialog(false);
  }

  function onDeleteDocumentRevision(
    document: BulkDocument,
    documentRevision: BulkDocumentRevision
  ): void {
    const documentIndex = bulkDocuments.findIndex(
      ({ refId }) => refId === document.refId
    );
    if (documentIndex >= 0) {
      // Collect delete revision to history record called `historyDeleteDocumentRevisions`
      const historyDocumentRevision: HistoryDeleteDocumentRevision = {
        refDocumentId: document.refId,
        projectDocumentRevision: documentRevision
      };
      setHistoryDeleteDocumentRevisions([
        historyDocumentRevision,
        ...historyDeleteDocumentRevisions
      ]);

      // Remove delete revision from selected projectDocumentRevisions
      const newDocumentRevisions = document.projectDocumentRevisions.filter(
        ({ refId }) => refId !== documentRevision.refId
      );
      const newDocument = {
        ...bulkDocuments[documentIndex],
        projectDocumentRevisions: newDocumentRevisions
      };
      const newDocuments = [
        ...bulkDocuments.slice(0, documentIndex),
        newDocument,
        ...bulkDocuments.slice(documentIndex + 1)
      ];
      setBulkDocuments(newDocuments);
    }
  }

  function onUndoDocumentRevision(document?: BulkDocument) {
    if (document) {
      const documentIndex = bulkDocuments.findIndex(
        ({ refId }) => refId === document.refId
      );
      if (documentIndex >= 0) {
        // Find the latest delete document revision
        const latestDocumentRevisionIndex =
          historyDeleteDocumentRevisions.findIndex(
            ({ refDocumentId }) => refDocumentId === document.refId
          );
        if (latestDocumentRevisionIndex >= 0) {
          const newDocumentRevisions = [
            ...document.projectDocumentRevisions,
            historyDeleteDocumentRevisions[latestDocumentRevisionIndex]
              .projectDocumentRevision
          ].sort((a, b) => a.refId - b.refId);
          const newDocument = {
            ...bulkDocuments[documentIndex],
            projectDocumentRevisions: newDocumentRevisions
          };
          const newDocuments = [
            ...bulkDocuments.slice(0, documentIndex),
            newDocument,
            ...bulkDocuments.slice(documentIndex + 1)
          ];
          setBulkDocuments(newDocuments);

          // Remove from history record
          const newBulkDeleteDocumentRevisions =
            historyDeleteDocumentRevisions.filter(
              (_, index) => index !== latestDocumentRevisionIndex
            );
          setHistoryDeleteDocumentRevisions(newBulkDeleteDocumentRevisions);
        }
      }
    } else {
      const latestDeleteDocumentRevision = historyDeleteDocumentRevisions[0];
      const document = bulkDocuments.find(
        ({ refId }) => refId === latestDeleteDocumentRevision.refDocumentId
      );
      if (latestDeleteDocumentRevision && document) {
        onUndoDocumentRevision(document);
      }
    }
  }

  async function onSubmit(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();

    // Add condition to check the value of bulkDocuments
    const isValidBulkDocuments = bulkDocuments.every((document) => {
      return (
        document.name &&
        document.no &&
        document.accessControlId &&
        document.documentCreatorId &&
        document.disciplineId &&
        document.documentId &&
        document.projectId &&
        document.projectDocumentRevisions.every((revision) => {
          return (
            revision.planIssueDate &&
            revision.planCompletedDate &&
            revision.manHour &&
            revision.purposeId &&
            revision.revisionId
          );
        })
      );
    });

    try {
      setLoading(true);
      if (isValidBulkDocuments) {
        await dispatch(
          bulkCreateProjectDocumentsAsync({
            projectDocuments: bulkDocuments as BulkProjectDocument[]
          })
        ).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: t('bulkPlanning.common.documentsCreatedSuccess')
          })
        );
        handleNext();
        setTimeout(() => {
          if (projectId) history.push(`/projects/${projectId}`);
        }, 1000);
      } else {
        throw new Error(t('bulkPlanning.common.checkAllFields') as string);
      }
    } catch (error) {
      setLoading(false);
      if (error instanceof Error)
        dispatch(
          loadingSlice.actions.error({
            message: error.message
          })
        );
    }
  }

  if (!projectId) {
    return <Redirect to="/projects" />;
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="createBulkPlanning"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          createBulkPlanning: 'POB'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('bulkPlanning.titles.POB')}
      </Typography>
      <Stepper
        className={classes.card}
        component={Paper}
        activeStep={activeStep}
        alternativeLabel
      >
        {stepKeys.map((key) => (
          <Step key={key}>
            <StepLabel>{t(key)}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <form onSubmit={onSubmit}>
        <Paper className={classes.card}>
          <Collapse in={activeStep === 0}>
            <div className={classes.flexSpace}>
              <div className={classes.flexGrow}>
              <Typography variant="h6" display="inline">
                {t('bulkPlanning.listOfDocuments')}
              </Typography>
                {selectedDocumentIds.length > 0 && (
                <Typography display="inline">
                  {' '}
                  ({selectedDocumentIds.length} {t('bulkPlanning.labels.selected')})
                </Typography>
                )}
              </div>
              <Paper className={classes.paperSearch} variant="outlined">
                <SearchRounded className={classes.colorTextSecondary} />
                <InputBase
                  placeholder={t('bulkPlanning.search') as string}
                  className={classes.searchInput}
                  type="search"
                  spellCheck={false}
                  fullWidth
                  value={params.search === null ? '' : params.search}
                  onChange={handleChangeSearch}
                  inputProps={{ 'aria-label': 'search' }}
                />
              </Paper>
            </div>
            <Divider className={classes.divider} />
            <TableContainer style={{ overflowY: 'hidden' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>{t('bulkPlanning.tableHeaders.documentNo')}</TableCell>
                    <TableCell>{t('bulkPlanning.tableHeaders.documentName')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {masterDataDocuments.length > 0 ? (
                    masterDataDocuments.map((document, index) => {
                      const selectedRow = getStatus(
                        document.id,
                        selectedDocumentIds
                      );
                      return (
                        <React.Fragment key={index}>
                          <TableRow
                            classes={{
                              root: clsx(classes.unsetAll, {
                                [classes.tableRowRoot]: selectedRow
                              }),
                              selected: clsx({
                                [classes.tableRowSelected]: selectedRow
                              })
                            }}
                            selected={selectedRow}
                            hover
                            onClick={() => handleSelectTemplate(document.id)}
                          >
                            <TableCell align="center" padding="checkbox">
                              <Checkbox color="primary" checked={selectedRow} />
                            </TableCell>
                            <TableCell>{document.no}</TableCell>
                            <TableCell>{document.name}</TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <TableRow className={classes.unsetAll}>
                      <TableCell className={classes.fileNotFound} colSpan={3}>
                        {getNotFoundText(
                          params.search,
                          'document',
                          'documents',
                          t
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
          <Collapse in={activeStep === 1}>
      <Typography variant="h6">{t('bulkPlanning.labels.documentNo')}</Typography>
            <Divider className={classes.divider} />
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Alert severity="info" icon={<AutoAwesomeRounded />}>
                  {t('bulkPlanning.common.systemUpdateDocuments')}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('bulkPlanning.plantCode') as string}
                  variant="outlined"
                  size="small"
                  name="plant"
                  fullWidth
                  value={code.plant ? code.plant : ''}
                  onChange={handleChangeTextField}
                  placeholder={t('bulkPlanning.maxCharacters') as string}
                />
              </Grid>
              {code.plant && (
                <Grid item xs={12}>
                  <Typography className={classes.px1} variant="caption">
                    {t('bulkPlanning.common.exampleDocumentNo', { originalNo, modifiedNo: modifiedNo(originalNo) })}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Divider className={classes.my2} />
            <div className={classes.header}>
              <Typography variant="h6">{t('bulkPlanning.labels.revisions')}</Typography>
              {!isEditRevision && (
                <Tooltip title={t('bulkPlanning.edit') as string} arrow>
                  <IconButton size="small" onClick={handleEditRevision}>
                    <EditRounded />
                  </IconButton>
                </Tooltip>
              )}
              {isEditRevision && (
                <Tooltip title={t('bulkPlanning.newRevision') as string} arrow>
                  <IconButton size="small" onClick={handleNewRevisionTemplate}>
                    <AddRounded />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <Divider className={classes.divider} />
            <Grid container spacing={1}>
              {!isEditRevision && (
                <Grid className={classes.revisionContainer} item xs={12}>
                  {documentRevisions.map((documentRevision, index) =>
                    generateRevisionChip(documentRevision, index)
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography className={classes.px1} variant="caption">
                  <b>{t('bulkPlanning.labels.definition')}:</b>{' '}
                  {purposes
                    .map(
                      (purpose) =>
                        `${purpose.abbreviation}: ${purpose.fullName}`
                    )
                    .join(', ')}
                </Typography>
              </Grid>
              {isEditRevision && (
                <>
                  {documentRevisions.map((documentRevision, index) => {
                    const purpose = getValue(
                      documentRevision.purposeId,
                      purposes
                    );
                    const revisionList = purpose?.revisions ?? [];
                    const revision = getValue(
                      documentRevision.revisionId,
                      revisionList
                    );

                    return (
                      <Grid
                        className={classes.flexSpace}
                        item
                        xs={12}
                        key={index}
                      >
                        <Autocomplete
                          className={classes.flexGrow2}
                          id="purpose"
                          options={purposes}
                          getOptionLabel={(option) =>
                            `${option.abbreviation}: ${option.fullName}`
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('bulkPlanning.tableHeaders.purpose')}
                              variant="outlined"
                              size="small"
                              required
                            />
                          )}
                          value={purpose}
                          onChange={(event, newValue) =>
                            handleChangeRevisionTemplate(
                              newValue?.id,
                              'purposeId',
                              index
                            )
                          }
                        />
                        <div
                          className={clsx(classes.flexSpace, classes.flexGrow)}
                        >
                          <Autocomplete
                            className={classes.flexGrow}
                            id="revision"
                            options={revisionList}
                            getOptionLabel={(option) => option.name}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('bulkPlanning.labels.revision')}
                                variant="outlined"
                                size="small"
                                required
                              />
                            )}
                            value={revision}
                            onChange={(event, newValue) =>
                              handleChangeRevisionTemplate(
                                newValue?.id,
                                'revisionId',
                                index
                              )
                            }
                          />
                          <div>
                            <Tooltip
                              title={
                                documentRevisions.length <= 1
                                  ? (t('bulkPlanning.common.cannotRemove') as string) : (t('bulkPlanning.common.remove') as string)
                              }
                              arrow
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={documentRevisions.length <= 1}
                                  onClick={() =>
                                    handleRemoveRevisionTemplate(index)
                                  }
                                >
                                  <CloseRounded />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                        </div>
                      </Grid>
                    );
                  })}
                  <Grid item xs={12} sm={6}>
                    <Button
                      color="primary"
                      variant="outlined"
                      fullWidth
                      onClick={handleResetRevisionTemplate}
                    >
                      {t('bulkPlanning.actions.reset')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      color="primary"
                      variant="contained"
                      disableElevation
                      fullWidth
                      disabled={documentRevisions.some(
                        ({ purposeId, revisionId }) => !purposeId || !revisionId
                      )}
                      onClick={handleEditRevision}
                    >
                      {t('bulkPlanning.actions.update')}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Collapse>
          <Collapse in={activeStep >= 2}>
            <div className={classes.flexSpace}>
              <div className={classes.flexGrow}>
                <Typography variant="h6" display="inline">
                  {t('bulkPlanning.labels.documentList')}
                </Typography>
                {bulkDocuments.length > 0 && (
                  <Typography display="inline">
                    {' '}
                    ({bulkDocuments.length} document
                    {bulkDocuments.length > 1 && 's'})
                  </Typography>
                )}
              </div>
              {bulkDocuments.length > 0 && (
                <ToggleButtonGroup
                  size="small"
                  value={getToggleStatus(
                    openedDocumentRefIds.length,
                    bulkDocuments.length
                  )}
                  exclusive
                  onChange={handleChangeDocumentView}
                >
                  <ToggleButton value="expand">
                    <UnfoldMoreRounded fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="collapse">
                    <UnfoldLessRounded fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </div>
            <Divider className={classes.divider} />
            {templateDocuments.length > 0 && bulkDocuments.length > 0 ? (
              <TableContainer style={{ overflowY: 'hidden' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="none" width={40} />
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          indeterminate={
                            selectedDocumentRefIds.length > 0 &&
                            selectedDocumentRefIds.length < bulkDocuments.length
                          }
                          checked={
                            selectedDocumentRefIds.length > 0 &&
                            selectedDocumentRefIds.length ===
                              bulkDocuments.length
                          }
                          onChange={handleSelectAllClick}
                        />
                      </TableCell>
                      <TableCell align="right" width={64}>
                        {t('bulkPlanning.tableHeaders.no')}
                      </TableCell>
                      <TableCell>{t('bulkPlanning.tableHeaders.documentName')}</TableCell>
                      <TableCell>{t('bulkPlanning.tableHeaders.documentNo')}</TableCell>
                      <TableCell>{t('bulkPlanning.tableHeaders.documentLevel')}</TableCell>
                      <TableCell className={classes.creatorField}>
                        {t('bulkPlanning.tableHeaders.documentCreator')}
                        <Badge
                          color="secondary"
                          variant="dot"
                          overlap="circle"
                          invisible={Boolean(anchorElement) || !isShowAllUsers}
                        >
                          <IconButton
                            size="small"
                            edge="end"
                            onClick={handleAnchorElement(true)}
                          >
                            <MoreVertRounded />
                          </IconButton>
                        </Badge>
                        <Menu
                          anchorEl={anchorElement}
                          keepMounted
                          getContentAnchorEl={null}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right'
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                          }}
                          open={Boolean(anchorElement)}
                          onClose={handleAnchorElement(false)}
                        >
                          <FormControlLabel
                            className={classes.marginFormControlLabel}
                            control={
                              <Checkbox
                                checked={isShowAllUsers}
                                onChange={handleChangeRelatedUsers}
                              />
                            }
                            label={t('bulkPlanning.showAllUsers') as string}
                          />
                        </Menu>
                      </TableCell>
                      <TableCell width={40} align="center" padding="none">
                        {selectedDocumentRefIds.length > 0 && (
                          <Tooltip title={t('bulkPlanning.tooltips.delete') as string} arrow>
                            <IconButton
                              size="small"
                              onClick={handleOpenDeleteAllDialog}
                            >
                              <DeleteRounded />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkDocuments.map((document, index) => {
                      const accessControl = getValue(
                        document.accessControlId,
                        accessControls
                      );
                      const documentTemplate = getValue(
                        document.documentId,
                        documents
                      );
                      const open = getStatus(
                        document.refId,
                        openedDocumentRefIds
                      );
                      const showError =
                        !open &&
                        document.projectDocumentRevisions.some(
                          (documentRevision) =>
                            !documentRevision.purposeId ||
                            !documentRevision.revisionId ||
                            !documentRevision.manHour ||
                            !documentRevision.planIssueDate ||
                            !documentRevision.planCompletedDate
                        );
                      const selectedRow = getStatus(
                        document.refId,
                        selectedDocumentRefIds
                      );
                      const selected = showError || selectedRow;
                      const canUndo =
                        selectedDocumentRefIds.length === 0 &&
                        historyDeleteDocumentRevisions.filter(
                          ({ refDocumentId }) =>
                            refDocumentId === document.refId
                        ).length > 0;

                      return (
                        <React.Fragment key={index}>
                          <TableRow
                            classes={{
                              root: clsx(classes.unsetAll, {
                                [selectedRow
                                  ? classes.tableRowRoot
                                  : classes.tableRowRootError]: selected
                              }),
                              selected: clsx({
                                [selectedRow
                                  ? classes.tableRowSelected
                                  : classes.tableRowSelectedError]: selected
                              })
                            }}
                            selected={selected}
                            hover
                          >
                            <TableCell align="center" padding="none">
                              <Tooltip title={(open ? t('bulkPlanning.common.collapse') : t('bulkPlanning.common.expand')) as string}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleOpenDocument(document.refId)
                                  }
                                >
                                  {open ? (
                                    <KeyboardArrowDownRounded />
                                  ) : (
                                    <KeyboardArrowRightRounded />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center" padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={selectedRow}
                                onClick={() =>
                                  handleSelectDocument(document.refId)
                                }
                              />
                            </TableCell>
                            <TableCell align="right">{index + 1}</TableCell>
                            <TableCell>
                              <TextField
                                className={clsx({
                                  [classes.errorInputUnderline]: !document.name
                                })}
                                id="document-name"
                                name="name"
                                multiline
                                fullWidth
                                helperText={
                                  documentTemplate &&
                                  documentTemplate.name !== document.name &&
                                  t('bulkPlanning.common.fromTemplate', { templateName: documentTemplate.name ?? '' })
                                }
                                value={document.name}
                                onChange={(event) =>
                                  handleChangeDocumentDetail(
                                    event.target.value,
                                    'name',
                                    index
                                  )
                                }
                                required
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                className={clsx({
                                  [classes.errorInputUnderline]: !document.no
                                })}
                                id="document-no"
                                name="no"
                                multiline
                                fullWidth
                                value={document.no}
                                onChange={(event) =>
                                  handleChangeDocumentDetail(
                                    event.target.value,
                                    'no',
                                    index
                                  )
                                }
                                required
                              />
                            </TableCell>
                            <TableCell className={classes.notClick}>
                              <Typography color="textSecondary">
                                {accessControl?.name ?? ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                className={clsx({
                                  [classes.errorInputUnderline]:
                                    !document.documentCreatorId
                                })}
                                id="document-creator"
                                options={getRelatedUser(
                                  document.userIds,
                                  users
                                )}
                                getOptionLabel={(option) =>
                                  `${option.firstName} ${option.lastName}`
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    required
                                  />
                                )}
                                value={getValue(
                                  document.documentCreatorId,
                                  users
                                )}
                                onChange={(event, newValue) =>
                                  handleChangeDocumentDetail(
                                    newValue?.id ?? '',
                                    'documentCreatorId',
                                    index
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell width={40} align="center" padding="none">
                              {selectedDocumentRefIds.length === 0 && (
                                <Tooltip title={t('bulkPlanning.tooltips.delete') as string} arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleOpenDeleteOneDialog(document)
                                    }
                                  >
                                    <DeleteRounded />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            classes={{
                              root: clsx(classes.unsetAll, {
                                [selectedRow
                                  ? classes.tableRowRoot
                                  : classes.tableRowRootError]: selected
                              }),
                              selected: clsx({
                                [selectedRow
                                  ? classes.tableRowSelected
                                  : classes.tableRowSelectedError]: selected
                              })
                            }}
                            selected={selected}
                          >
                            <TableCell padding="none" colSpan={8}>
                              <Collapse in={open}>
                                <Table size="small">
                                  <TableHead>
                                    <TableCell>{t('bulkPlanning.tableHeaders.purpose')}</TableCell>
                                    <TableCell>{t('bulkPlanning.tableHeaders.revision')}</TableCell>
                                    <TableCell
                                      align="center"
                                      padding="checkbox"
                                    >
                                      {t('bulkPlanning.tableHeaders.autoApproval')}
                                    </TableCell>
                                    <TableCell width={120}>{t('bulkPlanning.tableHeaders.manHour')}</TableCell>
                                    <TableCell>{t('bulkPlanning.tableHeaders.issueDate')}</TableCell>
                                    <TableCell>
                                      {t('bulkPlanning.tableHeaders.completedDate')}
                                      <HelpTooltip>
                                        {t('bulkPlanning.pob.completedDateHelp')}
                                      </HelpTooltip>
                                    </TableCell>
                                    <TableCell
                                      width={40}
                                      align="center"
                                      padding="none"
                                    >
                                      {canUndo && (
                                        <Tooltip title={t('bulkPlanning.tooltips.undo') as string} arrow>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              onUndoDocumentRevision(document)
                                            }
                                          >
                                            <UndoRounded />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableHead>
                                  <TableBody>
                                    {document.projectDocumentRevisions.map(
                                      (
                                        documentRevision,
                                        revisionIndex,
                                        documentRevisions
                                      ) => {
                                        const purpose = getValue(
                                          documentRevision.purposeId,
                                          purposes
                                        );
                                        const revisionList =
                                          purpose?.revisions ?? [];
                                        const revision = getValue(
                                          documentRevision.revisionId,
                                          revisionList
                                        );

                                        return (
                                          <TableRow
                                            className={classes.unsetAll}
                                            hover
                                            key={revisionIndex}
                                          >
                                            <TableCell>
                                              <Autocomplete
                                                className={clsx(
                                                  classes.flexGrow2,
                                                  {
                                                    [classes.errorInputUnderline]:
                                                      !purpose
                                                  }
                                                )}
                                                id="purpose"
                                                options={purposes}
                                                getOptionLabel={(option) =>
                                                  `${option.abbreviation}: ${option.fullName}`
                                                }
                                                renderInput={(params) => (
                                                  <TextField
                                                    {...params}
                                                    size="small"
                                                    required
                                                  />
                                                )}
                                                value={purpose}
                                                onChange={(event, newValue) =>
                                                  handleChangeDocumentRevisionDetail(
                                                    newValue?.id ?? '',
                                                    'purposeId',
                                                    index,
                                                    revisionIndex
                                                  )
                                                }
                                              />
                                            </TableCell>
                                            <TableCell width={128}>
                                              <Autocomplete
                                                className={clsx({
                                                  [classes.errorInputUnderline]:
                                                    !revision
                                                })}
                                                id="revision"
                                                options={revisionList}
                                                getOptionLabel={(option) =>
                                                  option.name
                                                }
                                                renderInput={(params) => (
                                                  <TextField
                                                    {...params}
                                                    size="small"
                                                    required
                                                  />
                                                )}
                                                value={revision}
                                                onChange={(event, newValue) =>
                                                  handleChangeDocumentRevisionDetail(
                                                    newValue?.id ?? '',
                                                    'revisionId',
                                                    index,
                                                    revisionIndex
                                                  )
                                                }
                                              />
                                            </TableCell>
                                            <TableCell
                                              className={clsx({
                                                [classes.notClick]:
                                                  documentRevision.purposeId !==
                                                  PurposeLabel.IFR
                                              })}
                                              align="center"
                                              padding="checkbox"
                                            >
                                              {documentRevision.purposeId ===
                                                PurposeLabel.IFR && (
                                                <Checkbox
                                                  checked={
                                                    documentRevision.isAutoApproval
                                                  }
                                                  color="primary"
                                                  disabled={
                                                    documentRevision.purposeId !==
                                                    PurposeLabel.IFR
                                                  }
                                                  onChange={(event) =>
                                                    handleChangeDocumentRevisionDetail(
                                                      event.target.checked,
                                                      'isAutoApproval',
                                                      index,
                                                      revisionIndex
                                                    )
                                                  }
                                                />
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              <TextField
                                                className={clsx({
                                                  [classes.errorInputUnderline]:
                                                    !documentRevision.manHour
                                                })}
                                                type="number"
                                                size="small"
                                                value={
                                                  documentRevision.manHour
                                                    ? documentRevision.manHour
                                                    : ''
                                                }
                                                onChange={(event) =>
                                                  handleChangeDocumentRevisionDetail(
                                                    event.target.value,
                                                    'manHour',
                                                    index,
                                                    revisionIndex,
                                                    { positiveNumber: true }
                                                  )
                                                }
                                                required
                                              />
                                            </TableCell>
                                            <TableCell width={200}>
                                              <KeyboardDatePicker
                                                className={clsx({
                                                  [classes.errorInputUnderline]:
                                                    !documentRevision.planIssueDate
                                                })}
                                                autoOk
                                                size="small"
                                                variant="inline"
                                                InputAdornmentProps={{
                                                  position: 'end'
                                                }}
                                                value={
                                                  documentRevision.planIssueDate
                                                }
                                                onChange={(event) =>
                                                  handleChangeDocumentRevisionDetail(
                                                    event
                                                      ? moment(event)
                                                          .endOf('day')
                                                          .toDate()
                                                      : null,
                                                    'planIssueDate',
                                                    index,
                                                    revisionIndex
                                                  )
                                                }
                                                KeyboardButtonProps={{
                                                  'aria-label':
                                                    'change plan issue date'
                                                }}
                                                required
                                              />
                                            </TableCell>
                                            <TableCell width={200}>
                                              <KeyboardDatePicker
                                                className={clsx({
                                                  [classes.errorInputUnderline]:
                                                    !documentRevision.planIssueDate
                                                })}
                                                autoOk
                                                size="small"
                                                variant="inline"
                                                InputAdornmentProps={{
                                                  position: 'end'
                                                }}
                                                minDate={
                                                  documentRevision.planIssueDate
                                                }
                                                minDateMessage={t('bulkPlanning.common.dateValidation')}
                                                value={
                                                  documentRevision.planCompletedDate
                                                }
                                                onChange={(event) =>
                                                  handleChangeDocumentRevisionDetail(
                                                    event
                                                      ? moment(event)
                                                          .endOf('day')
                                                          .toDate()
                                                      : null,
                                                    'planCompletedDate',
                                                    index,
                                                    revisionIndex
                                                  )
                                                }
                                                KeyboardButtonProps={{
                                                  'aria-label':
                                                    'change complete issue date'
                                                }}
                                                required
                                              />
                                            </TableCell>
                                            <TableCell
                                              width={40}
                                              align="center"
                                              padding="none"
                                            >
                                              {selectedDocumentRefIds.length ===
                                                0 && (
                                                <Tooltip
                                                  title={
                                                    documentRevisions.length <=
                                                    1
                                                      ? (t('bulkPlanning.common.cannotDelete') as string) : (t('bulkPlanning.actions.delete') as string)
                                                  }
                                                  arrow
                                                >
                                                  <span>
                                                    <IconButton
                                                      size="small"
                                                      disabled={
                                                        documentRevisions.length <=
                                                        1
                                                      }
                                                      onClick={() =>
                                                        onDeleteDocumentRevision(
                                                          document,
                                                          documentRevision
                                                        )
                                                      }
                                                    >
                                                      <DeleteRounded />
                                                    </IconButton>
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      }
                                    )}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : templateDocuments.length === 0 ? (
              <div className={classes.notFoundError}>
                <Typography gutterBottom>{t('bulkPlanning.oops')}</Typography>
                <Typography component="div" variant="caption">
                  {t('bulkPlanning.common.oopsCannotDisplay')}
                </Typography>
                <Typography component="div" variant="caption" gutterBottom>
                  {t('bulkPlanning.common.checkMasterData')}
                </Typography>
              </div>
            ) : (
              <div className={classes.notFoundError}>
                <Typography gutterBottom>{t('bulkPlanning.omg')}</Typography>
                <Typography component="div" variant="caption">
                  {t('bulkPlanning.common.omgDeletedAll')}
                </Typography>
                <Typography component="div" variant="caption" gutterBottom>
                  {t('bulkPlanning.common.clickResetDefault')}
                </Typography>
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={onReset}
                >
                  {t('bulkPlanning.actions.reset')}
                </Button>
              </div>
            )}
          </Collapse>
        </Paper>
        <div className={classes.actionButton}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<KeyboardArrowLeftRounded />}
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            {t('projectCreate.back')}
          </Button>
          {activeStep < stepKeys.length - 1 ? (
            <Button
              variant="contained"
              color="primary"
              size="large"
              endIcon={<KeyboardArrowRightRounded />}
              disabled={nextButton(activeStep).disabled}
              onClick={nextButton(activeStep).action}
            >
              {t('projectCreate.next')}
            </Button>
          ) : (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={nextButton(activeStep).disabled}
            >
              {t('bulkPlanning.actions.createDocuments')}
            </Button>
          )}
        </div>
      </form>
      <ConfirmDeleteBox
        open={openDeleteOneDialog}
        text={selectedDocument?.name ?? t('bulkPlanning.common.untitledDocument') as string}
        onDelete={onDeleteDocument}
        onClose={handleOpenDeleteOneDialog}
      />
      <ConfirmDeleteBox
        open={openDeleteAllDialog}
        text={String(t('bulkPlanning.listOfDocuments')) as string}
        onDelete={onDeleteSelectedDocuments}
        onClose={handleOpenDeleteAllDialog}
      >
        <ul className={classes.ul}>
          {selectedDocumentRefIds.map((refId) => {
            const document = bulkDocuments.find(
              (document) => document.refId === refId
            );
            return <li key={refId}>{document?.name ?? t('bulkPlanning.common.untitledDocument')}</li>;
          })}
        </ul>
      </ConfirmDeleteBox>
    </div>
  );
}

function UnfoldMoreRounded(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="m12 5.83 2.46 2.46c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L12.7 3.7a.9959.9959 0 0 0-1.41 0L8.12 6.88c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 5.83zm0 12.34-2.46-2.46a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l3.17 3.18c.39.39 1.02.39 1.41 0l3.17-3.17c.39-.39.39-1.02 0-1.41a.9959.9959 0 0 0-1.41 0L12 18.17z" />
    </SvgIcon>
  );
}

function UnfoldLessRounded(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="M8.12 19.3c.39.39 1.02.39 1.41 0L12 16.83l2.47 2.47c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41l-3.17-3.17a.9959.9959 0 0 0-1.41 0l-3.17 3.17c-.4.38-.4 1.02-.01 1.41zm7.76-14.6a.9959.9959 0 0 0-1.41 0L12 7.17 9.53 4.7a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.03 0 1.42l3.17 3.17c.39.39 1.02.39 1.41 0l3.17-3.17c.4-.39.4-1.03.01-1.42z" />
    </SvgIcon>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { margin: theme.spacing(1.5, 0, 12, 0) },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    paperSearch: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 2, 1, 1),
      borderRadius: 18,
      width: 240,
      height: 36,
      zIndex: 2
    },
    searchInput: {
      marginLeft: theme.spacing(1)
    },
    colorTextSecondary: {
      color: theme.palette.text.secondary
    },
    notClick: { cursor: 'not-allowed' },
    unsetAll: {
      '& > *': {
        borderBottom: 'unset'
      }
    },
    tableRowRoot: {
      '&$tableRowSelected, &$tableRowSelected:hover': {
        backgroundColor: fade(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        )
      }
    },
    tableRowSelected: {
      backgroundColor: fade(
        theme.palette.primary.main,
        theme.palette.action.selectedOpacity
      )
    },
    tableRowRootError: {
      '&$tableRowSelected, &$tableRowSelected:hover': {
        backgroundColor: fade(
          theme.palette.secondary.main,
          theme.palette.action.selectedOpacity
        )
      }
    },
    tableRowSelectedError: {
      backgroundColor: fade(
        theme.palette.secondary.main,
        theme.palette.action.selectedOpacity
      )
    },
    errorInputUnderline: {
      '& .MuiInput-underline:before, & .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInput-underline:after':
        {
          borderBottomColor: theme.palette.error.main,
          borderBottomWidth: 2
        }
    },
    creatorField: {
      padding: theme.spacing(0.75, 2)
    },
    revisionContainer: {
      display: 'flex',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      marginBottom: theme.spacing(1)
    },
    flexSpace: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center'
    },
    truncatedChip: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%' // Adjust this to control the maximum width of the Chip
    },
    notFoundError: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: theme.spacing(1, 2)
    },
    fileNotFound: {
      padding: theme.spacing(2, 1),
      textAlign: 'center'
    },
    marginFormControlLabel: { margin: theme.spacing(0, 1.5, 0, 0) },
    ul: { paddingLeft: theme.spacing(2) },
    actionButton: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    flexGrow: { flexGrow: 1 },
    flexGrow2: { flexGrow: 2 },
    my2: { margin: theme.spacing(2, 0) },
    px1: { padding: theme.spacing(0, 1) }
  })
);
